import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import { useContract } from '../../context/ContractContext';
import { useNotification } from '../../context/NotificationContext';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import Button from '../../components/common/Button/Button';
import Input from '../../components/common/Input/Input';
import { formatNumber } from '../../utils/formatters';
import './Withdraw.css';

const Withdraw = () => {
  const navigate = useNavigate();
  const { isConnected, isInitializing } = useWallet();
  const { 
    isRegistered,
    isCheckingRegistration,
    userId,
    withdraw,
    getAvailableBreakdown,
    getIncomeInfo,
    getWithdrawConfig,
    isLoading
  } = useContract();
  const { error: showError, success } = useNotification();

  const [amount, setAmount] = useState('');
  const [availableData, setAvailableData] = useState(null);
  const [incomeData, setIncomeData] = useState(null);
  const [withdrawConfig, setWithdrawConfig] = useState({ minWithdraw: '0', deductionBps: 1000 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isInitializing || isCheckingRegistration) return;
    
    if (!isConnected) {
      navigate('/');
      return;
    }
    if (!isRegistered) {
      navigate('/register');
      return;
    }
  }, [isConnected, isRegistered, isInitializing, isCheckingRegistration, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      
      try {
        const [available, income, config] = await Promise.all([
          getAvailableBreakdown(),
          getIncomeInfo(),
          getWithdrawConfig()
        ]);

        setAvailableData(available);
        setIncomeData(income);
        setWithdrawConfig(config);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
      setLoading(false);
    };

    fetchData();
  }, [userId]);

  const handleWithdraw = async () => {
    const withdrawAmount = parseFloat(amount);
    const maxAvailable = parseFloat(availableData?.totalAvailable || 0);

    if (!amount || withdrawAmount <= 0) {
      showError('Please enter a valid amount');
      return;
    }

    if (withdrawAmount > maxAvailable) {
      showError('Insufficient available balance');
      return;
    }

    if (withdrawAmount < minWithdraw) {
      showError(`Minimum withdrawal is ${formatNumber(minWithdraw)} USDT`);
      return;
    }

    const result = await withdraw(amount);
    if (result) {
      setAmount('');
      // Refresh data
      const [available, income] = await Promise.all([
        getAvailableBreakdown(),
        getIncomeInfo()
      ]);
      setAvailableData(available);
      setIncomeData(income);
    }
  };

  const handleMaxAmount = () => {
    setAmount(availableData?.totalAvailable || '0');
  };

  const totalAvailable = parseFloat(availableData?.totalAvailable || 0);
  const minWithdraw = parseFloat(withdrawConfig.minWithdraw || 0);
  const deductionPercent = withdrawConfig.deductionBps / 100;
  const enteredAmount = parseFloat(amount) || 0;
  const deductionAmount = (enteredAmount * withdrawConfig.deductionBps) / 10000;
  const receiveAmount = enteredAmount - deductionAmount;
  const canWithdraw = enteredAmount > 0 && enteredAmount >= minWithdraw && enteredAmount <= totalAvailable;

  return (
    <PageContainer title="Withdraw">
      <div className="withdraw-page">
        <div className="withdraw-balance-card">
          <div className="balance-header">
            <span className="balance-label">Available Balance</span>
            <span className="balance-value">{formatNumber(availableData?.totalAvailable || 0)}</span>
            <span className="balance-unit">USDT</span>
          </div>

          <div className="balance-breakdown">
            <div className="breakdown-item">
              <span className="breakdown-icon">📈</span>
              <span className="breakdown-label">ROI</span>
              <span className="breakdown-value">{formatNumber(availableData?.availableROI || 0)}</span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-icon">⚡</span>
              <span className="breakdown-label">Binary</span>
              <span className="breakdown-value">{formatNumber(availableData?.availableBinary || 0)}</span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-icon">👥</span>
              <span className="breakdown-label">Referral</span>
              <span className="breakdown-value">{formatNumber(availableData?.availableReferral || 0)}</span>
            </div>
          </div>
        </div>

        <div className="withdraw-form">
          <div className="form-header">
            <h3>Withdraw Amount</h3>
            <button className="max-btn" onClick={handleMaxAmount}>MAX</button>
          </div>

          <Input
            type="number"
            placeholder="Enter amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            suffix="USDT"
          />

          <div className="history-stats withdraw-summary">
            <div className="history-stat">
              <span className="stat-label">Deduction ({deductionPercent}%)</span>
              <span className="stat-value">-{formatNumber(deductionAmount)} USDT</span>
            </div>
            <div className="history-stat">
              <span className="stat-label">You Receive</span>
              <span className="stat-value">{formatNumber(receiveAmount)} USDT</span>
            </div>
            {minWithdraw > 0 && (
              <div className="history-stat">
                <span className="stat-label">Minimum</span>
                <span className="stat-value">{formatNumber(minWithdraw)} USDT</span>
              </div>
            )}
          </div>

          <Button
            fullWidth
            size="lg"
            onClick={handleWithdraw}
            loading={isLoading}
            disabled={!canWithdraw}
          >
            Withdraw
          </Button>
        </div>

        <div className="withdraw-history">
          <h3 className="section-title">Withdrawal Stats</h3>
          <div className="history-stats">
            <div className="history-stat">
              <span className="stat-label">Total Withdrawn</span>
              <span className="stat-value">{formatNumber(incomeData?.totalWithdrawn || 0)} USDT</span>
            </div>
            <div className="history-stat">
              <span className="stat-label">ROI Withdrawn</span>
              <span className="stat-value">{formatNumber(incomeData?.roiWithdrawn || 0)} USDT</span>
            </div>
            <div className="history-stat">
              <span className="stat-label">Binary Withdrawn</span>
              <span className="stat-value">{formatNumber(incomeData?.binaryWithdrawn || 0)} USDT</span>
            </div>
            <div className="history-stat">
              <span className="stat-label">Referral Withdrawn</span>
              <span className="stat-value">{formatNumber(incomeData?.referralWithdrawn || 0)} USDT</span>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default Withdraw;